"use client";
import { grantConsent } from "@/lib/analytics";
import { captureAttribution } from "@/lib/attribution";
import { useEffect, useState, useRef } from "react";
import FAIcon from "@/components/icons/faIcon";
import Image from "next/image";
import Link from "next/link";

const expirationTime = 6 * 30 * 24 * 60 * 60 * 1000; // 6 months

const privacyPolicySections = [
  {
    title: "1. Important Notice",
    content: [
      "This Privacy & Cookie Policy explains how Safety Management Ltd collects, uses, and protects your personal data, why we are legally permitted to process it, who may access it, and what rights you have.",
      "If a separate Privacy Statement is published on our Site homepage and conflicts with this Policy, the separate Privacy Statement will take precedence.",
      "Safety Management Ltd acts as the data controller of the personal data you provide to us.",
      "You have the right to object to processing carried out for legitimate business interests or direct marketing purposes. Please see the section titled 'Your Rights' for further details.",
    ],
  },
  {
    title: "2. Personal Data We May Collect and Why",
    content: [
      "Personal data refers to information that identifies you directly or indirectly.",
      "We may collect the following categories of personal data:",
      "Contact details – including your name, email address, and telephone number to respond to enquiries or provide services.",
      "Correspondence – information you provide when contacting us via email, telephone, post, or through our Site.",
      "Survey responses – if you choose to participate in research surveys.",
      "Transaction details – information relating to purchases or transactions carried out through our Site.",
      "Website usage data – including traffic data, location data, weblogs, and communication data relating to your visits.",
    ],
  },
  {
    title: "3. Cookies",
    content: [
      "We use cookies and similar technologies to collect information about your use of our Site.",
      "Cookies are small files stored on your device that help websites recognise users and remember preferences.",
      "Essential Cookies – Required for the Site to function properly, including account access and cookie preference settings.",
      "Analytics Cookies – Help us understand how visitors use our Site so we can improve performance and user experience.",
      "User Preference Cookies – Remember your settings and preferences.",
      "Social Sharing Cookies – Enable content sharing via social platforms.",
      "Interest-Based Advertising Cookies – Used by third parties to deliver relevant advertisements.",
      "You can refuse cookies by adjusting your browser settings. However, some features of the Site may not function properly without them.",
      "For more information on managing cookies, visit www.allaboutcookies.org.",
    ],
  },
  {
    title: "4. How We Use Your Personal Data",
    content: [
      "To provide products and services you have requested.",
      "To communicate with you regarding services, updates, or changes to our terms.",
      "To send marketing communications where you have provided consent or where legally permitted.",
      "To track usage of our Site to improve functionality and personalisation.",
      "To provide and improve customer support.",
      "To maintain accurate internal records.",
      "To respond to enquiries, complaints, or disputes.",
      "To comply with legal obligations, including fraud prevention and investigation.",
    ],
  },
  {
    title: "5. Legal Grounds for Processing",
    content: [
      "We process your personal data based on one or more of the following legal grounds:",
      "Consent – where you have given clear permission for specific processing activities.",
      "Contractual necessity – where processing is required to fulfil our agreement with you.",
      "Legal obligation – where we must comply with applicable laws.",
      "Legitimate interests – where processing supports our business interests and does not override your rights and freedoms.",
    ],
  },
  {
    title: "6. Who We Share Your Personal Data With",
    content: [
      "We may share your personal data with trusted suppliers and service providers acting on our behalf.",
      "We may disclose personal data to comply with legal obligations or to protect the rights, property, or safety of Safety Management Ltd, our customers, or others.",
      "Where personal data is transferred outside the UK or European Economic Area, we ensure appropriate safeguards are in place to protect it.",
    ],
  },
  {
    title: "7. How Long We Keep Your Personal Data",
    content: [
      "We retain personal data only for as long as necessary to fulfil the purposes for which it was collected.",
      "Retention periods may depend on legal requirements, limitation periods, potential disputes, and regulatory guidance.",
      "When data is no longer required, it is securely deleted.",
    ],
  },
  {
    title: "8. Your Rights",
    content: [
      "You have the following rights under data protection law:",
      "The right to be informed about how your data is used.",
      "The right of access to your personal data.",
      "The right to rectification of inaccurate or incomplete data.",
      "The right to erasure in certain circumstances.",
      "The right to restrict processing in specific situations.",
      "The right to data portability where applicable.",
      "The right to object to processing based on legitimate interests or direct marketing.",
      "The right to withdraw consent at any time.",
      "The right to lodge a complaint with the Information Commissioner's Office (ICO).",
    ],
  },
  {
    title: "9. How to Contact Us",
    content: [
      "If you wish to exercise your rights or have concerns about how we handle your personal data, please contact us using the details provided on www.sml.co.uk.",
      "You may also contact the Information Commissioner's Office (ICO) if you are dissatisfied with our response.",
    ],
  },
  {
    title: "10. Links to Other Websites",
    content: [
      "Our Site may contain links to third-party websites.",
      "This Privacy Policy does not apply to those websites and we are not responsible for their privacy practices.",
    ],
  },
  {
    title: "11. Changes to This Privacy Policy",
    content: [
      "We may update this Privacy & Cookie Policy from time to time.",
      "You are encouraged to review this page periodically to stay informed of any changes.",
    ],
  },
];

const CookieConsent = () => {
  const [consentModal, setShowConsentModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState("welcome");

  const [preferences, setPreferences] = useState({
    required: true,
    analytics: false,
    marketing: false,
  });

  const getConsent = () => {
    try {
      return JSON.parse(localStorage?.getItem?.("sml_cc") ?? "{}");
    } catch {
      return {};
    }
  };

  const getConsentTimestamp = () => {
    try {
      return localStorage?.getItem?.("sml_cc_timestamp") ?? null;
    } catch {
      return null;
    }
  };

  const setConsent = (preference) => {
    localStorage.setItem("sml_cc", JSON.stringify(preference));
    setConsentTimestamp();
  };

  const setConsentTimestamp = () => {
    localStorage.setItem("sml_cc_timestamp", Date.now());
  };

  const clearConsent = () => {
    localStorage.removeItem("sml_cc");
    localStorage.removeItem("sml_cc_timestamp");
  };

  const handleAcceptAll = () => {
    setShowConsentModal(false);
    const fullConsent = {
      ...preferences,
      required: true,
      analytics: true,
      marketing: true,
    };
    setConsent(fullConsent);
    grantConsent(fullConsent);
    captureAttribution();
  };

  const handleConfirmChoices = () => {
    setConsent(preferences);
    setShowConsentModal(false);
    if (preferences.analytics || preferences.marketing) {
      grantConsent({ ...preferences });
    }
    if (preferences.marketing) {
      captureAttribution();
    }
  };

  const handleRejectAll = () => {
    setConsent({ required: true, analytics: false, marketing: false });
    setShowConsentModal(false);
  };

  const handleOptionalChange = (category) => {
    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  useEffect(() => {
    const consent = getConsent();
    const timestamp = getConsentTimestamp();
    const isExpired = !timestamp || Date.now() - timestamp >= expirationTime;
    setShowConsentModal(!consent || isExpired);
    if (isExpired) {
      clearConsent();
    } else if (consent?.required) {
      grantConsent({ ...consent });
    }
  }, []);

  // Robust scroll-lock while modal is open (preserve scroll position, works on mobile)
  const _scrollY = useRef(0);
  useEffect(() => {
    if (typeof document === "undefined") return;

    if (consentModal) {
      // store current scroll position
      _scrollY.current = window.scrollY || window.pageYOffset || 0;

      // compensate for missing scrollbar to avoid layout shift
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      // lock scroll by fixing body in place
      document.body.style.position = "fixed";
      document.body.style.top = `-${_scrollY.current}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.overflow = "hidden";
    } else {
      // restore
      const prevScroll = _scrollY.current;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      window.scrollTo(0, prevScroll);
    }

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [consentModal]);

  const ToggleSwitch = ({ label, description, checked, onChange }) => {
    return (
      <label className="inline-flex flex-row-reverse gap-6 items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={onChange}
        />
        <div className="relative w-full max-w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer bg-gray-200 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-transform duration-300 ease-in-out border-gray-300 peer-checked:bg-primary"></div>
        <div className="ms-3">
          <span className="text-sm font-bold text-gray-800">{label}</span>
          {description && (
            <p className="text-sm text-gray-500">{description}</p>
          )}
        </div>
      </label>
    );
  };

  return consentModal ? (
    <div className="fixed inset-0 flex items-center justify-center z-[9999]">
      <div className="absolute w-full h-full bg-black/50"></div>
      <div className="bg-white shadow-2xl shadow-black/20 rounded-xl z-[9999] my-6 mx-4 max-w-full sm:max-w-148.75 p-5 px-6 md:p-7 overflow-y-auto max-h-[90vh]">
        <div className="flex flex-col">
          <div className="mb-4 flex justify-center py-3">
            <Image
              width={140}
              height={60}
              className="h-auto"
              src="/images/logo/sml-logo-wide-svg.svg"
              alt="Safety Management Ltd"
            />
          </div>

          {selectedTab === "customise" && (
            <div className="space-y-4 text-[14.5px] mb-4">
              <div className="mb-4 text-gray-600">
                Cookies are small text files placed on your device as you
                browse. They store information about your preferences or device
                to enhance your experience. While they don&apos;t usually identify
                you, you can choose which cookies to allow, as we value your
                privacy.
              </div>
              <ToggleSwitch
                label="Strictly necessary cookies"
                description="These cookies are essential for you to browse our website and use its features and cannot be disabled"
                checked={preferences.required}
                onChange={() => {}}
              />
              <ToggleSwitch
                label="Analytics Cookies"
                description="These cookies allow us to track visits and traffic sources so we can measure and improve the performance of our website"
                checked={preferences.analytics}
                onChange={() => handleOptionalChange("analytics")}
              />
              <ToggleSwitch
                label="Marketing Cookies"
                description="These cookies track your online activity to help our advertising partners deliver more relevant advertising or to limit how many times you see ads"
                checked={preferences.marketing}
                onChange={() => handleOptionalChange("marketing")}
              />
            </div>
          )}

          {selectedTab === "welcome" && (
            <div className="flex text-[15.5px] flex-col text-gray-700">
              <div className="text-lg pb-2 font-bold text-gray-900">
                Cookies: Your Privacy, Our Priority
              </div>
              <div>
                Safety Management Ltd and our partners would like to use cookies
                on this site to:
              </div>
              <ul className="list-disc pl-6 text-gray-500 py-5">
                <li>Allow you to browse our website and use its features</li>
                <li>Analyse traffic and performance to improve our website</li>
                <li>
                  Show relevant content and ads to enhance your experience
                </li>
              </ul>
              <div>
                You can choose to accept all cookies, reject all non-essential
                cookies or customise your preferences below.
              </div>
            </div>
          )}

          {selectedTab === "policy" && (
            <div
              className="overflow-y-auto max-h-[50vh] pr-1"
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
              style={{ touchAction: "auto" }}
              tabIndex={0}
            >
              <div className="text-lg font-bold text-gray-900 mb-4">
                Privacy &amp; Cookie Policy
              </div>
              <div className="text-xs text-gray-400 mb-6">
                Last updated: 1 February 2026
              </div>
              {privacyPolicySections.map((section, idx) => (
                <div key={idx} className="mb-6">
                  <h2 className="text-sm font-semibold mb-2 text-gray-900">
                    {section.title}
                  </h2>
                  <div className="flex text-sm flex-col gap-2">
                    {section.content.map((paragraph, pIdx) => (
                      <p key={pIdx} className="text-gray-600">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
              <div className="text-sm text-gray-500 mt-4">
                View the full policy at{" "}
                <Link
                  href="/privacy-and-cookies"
                  className="text-primary underline"
                  target="_blank"
                >
                  sml.co.uk/privacy-and-cookies
                </Link>
              </div>
            </div>
          )}

          {selectedTab !== "policy" && (
            <div className="mt-2 mb-1">
              <button
                title="Privacy Policy"
                className="text-sm text-primary underline"
                onClick={() => setSelectedTab("policy")}
              >
                View our Privacy Policy
              </button>
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 md:flex-row justify-center mt-5 mb-1 w-full">
            {selectedTab === "welcome" ? (
              <button
                aria-label="Manage Cookies"
                className="grow border border-gray-300 text-gray-700 py-2 px-4 rounded-full text-sm hover:bg-gray-50 transition-colors"
                onClick={() => setSelectedTab("customise")}
              >
                <FAIcon icon="cookie-bite" size={14} button />
                Manage Cookies
              </button>
            ) : (
              <button
                aria-label="Back"
                className="grow border border-gray-300 text-gray-700 py-2 px-4 rounded-full text-sm hover:bg-gray-50 transition-colors"
                onClick={() => setSelectedTab("welcome")}
              >
                <FAIcon icon="arrow-left" size={14} button />
                Back
              </button>
            )}
            <button
              aria-label="Reject all"
              className="grow border border-gray-300 text-gray-700 py-2 px-4 rounded-full text-sm hover:bg-gray-50 transition-colors"
              onClick={handleRejectAll}
            >
              <FAIcon icon="ban" size={14} button />
              Reject all
            </button>
            {selectedTab === "customise" ? (
              <button
                aria-label="Confirm choices"
                className="grow bg-primary border border-primary text-accent py-2 px-4 rounded-full text-sm hover:bg-primary/90 transition-colors"
                onClick={handleConfirmChoices}
              >
                <FAIcon icon="check" size={14} button />
                Confirm
              </button>
            ) : (
              <button
                aria-label="Accept all"
                className="grow bg-primary border border-primary text-accent py-2 px-4 rounded-full text-sm hover:bg-primary/90 transition-colors"
                onClick={handleAcceptAll}
              >
                <FAIcon icon="check" size={14} button />
                Accept all
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

export default CookieConsent;
